"use client";

import { motion } from "framer-motion";
import CountryFlag from "react-country-flag";
import { InfiniteData } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useWindowSize } from "../../hooks/useWindowSize";
import LeaderboardLoading from "./LeaderboardLoading";
import { groupInnerVariants } from "./animations";
import { JSX } from "react";
import clsx from "clsx";

interface CTPlayerData {
  playerId: string;
  playername: string;
  gender: string;
  nationality?: string;
  total: {
    distanceToPin: number;
    pos: number;
  };
  holes: [{ holeNumber: number; distanceToPin: number }];
}

interface CTPLeaderboardData {
  data: {
    node: {
      aggregatedClosestToPinLeaderboard: {
        records: {
          items: [CTPlayerData];
        };
      };
    };
  };
}

interface TournamentData {
  data: {
    node: {
      rounds: {
        embeddedGame: {
          closestToPin: {
            holes: [number];
          };
        };
      }[];
    };
  };
}

interface CTPLeaderboardProps {
  isMultiRoundTournament?: boolean;
  gender?: string;
  selectedPlayers: string[];
  setSelectedPlayers: (ids: string[]) => void;
  tournamentData: undefined | AxiosResponse<TournamentData, any>;
  data: undefined | InfiniteData<AxiosResponse<CTPLeaderboardData, any>>;
  isLoading: boolean;
  searchText?: string;
  ErrorMessage: () => JSX.Element | null;
}

const CTPLeaderboard = ({
  isMultiRoundTournament,
  tournamentData,
  data,
  gender,
  selectedPlayers,
  setSelectedPlayers,
  isLoading,
  searchText,
  ErrorMessage,
}: CTPLeaderboardProps) => {
  const { windowSize } = useWindowSize();

  return (
    <div
      role="table"
      className="bg-[#2c2c2c] rounded-[10px] overflow-hidden min-h-[800px]"
    >
      <div
        role="thead"
        className={`
          flex justify-between gap-[5px] md:gap-2.5 bg-tm-c-gray-700
          px-2.5 py-0 lg:p-0
          [&>div]:py-[15px] [&>div]:px-0 [&>div]:flex-1 [&>div]:text-center [&>div]:text-xs lg:[&>div]:text-[15px]
        `}
      >
        <div role="th">Pos</div>
        <div role="th">
          <span className="hidden lg:block">Country</span>
        </div>
        <div role="th" className="!flex-[4] !text-left md:!flex-[5]">
          Player
        </div>
        {windowSize.width > 860 &&
          tournamentData?.data?.data?.node?.rounds[
            isMultiRoundTournament ? 1 : 0
          ]?.embeddedGame?.closestToPin?.holes?.map((hole: number) => (
            <div
              role="th"
              className="first-of-type:!flex-[2] md:first-of-type:!flex-1"
              key={hole}
            >
              Hole {hole}
            </div>
          ))}
        <div role="th" className="md:!pr-[15px]">
          Total
        </div>
      </div>
      <div role="tbody">
        <ErrorMessage />

        {data?.pages?.map((page: { data: CTPLeaderboardData }, x: number) => {
          const itemsLength =
            page.data.data.node.aggregatedClosestToPinLeaderboard?.records
              ?.items.length;

          if (x === 0 && !itemsLength && !searchText) {
            return (
              <h2
                key={x}
                className="text-tm-c-dark opacity-60 text-[25px] mt-[160px] mx-10 mb-0 text-center"
              >
                No players have hit all the required greens yet
              </h2>
            );
          }

          if (x === 0 && !itemsLength && searchText) {
            return (
              <h2
                key={x}
                className="text-tm-c-dark opacity-60 text-[25px] mt-[160px] mx-10 mb-0 text-center"
              >
                No results found
              </h2>
            );
          }

          return page.data.data.node.aggregatedClosestToPinLeaderboard?.records?.items.map(
            (player: CTPlayerData) => {
              const convertMToFeet = (distanceInMeters: number) => {
                const inches = distanceInMeters * 39.37;
                const feet = Math.floor(inches / 12);
                const remainingInches = Math.floor(inches % 12);
                return `${feet}' ${remainingInches}"`;
              };
              return (
                <div
                  role="tr"
                  className={clsx(
                    "flex flex-col justify-between overflow-hidden opacity-100 transition-opacity duration-300",
                    {
                      "opacity-20":
                        gender &&
                        player.gender &&
                        player.gender.toLowerCase() !== gender.toLowerCase(),
                    }
                  )}
                  key={player.playerId}
                >
                  <div
                    role="tr"
                    className={`
                      flex justify-between gap-[5px] md:gap-2.5
                      w-full py-0 px-2.5 lg:p-0
                      [&>div]:flex [&>div]:items-center [&>div]:justify-center [&>div]:flex-1 [&>div]:relative
                      [&>div]:text-center [&>div]:text-xs md:[&>div]:text-[15px]
                      [&>div]:py-[15px] [&>div]:px-0 md:[&>div]:last-of-type:pr-[15px]
                    `}
                    onClick={() => {
                      if (
                        windowSize.width > 860 ||
                        (gender &&
                          player.gender &&
                          player.gender.toLowerCase() !== gender.toLowerCase())
                      )
                        return;
                      if (selectedPlayers.includes(player.playerId)) {
                        setSelectedPlayers(
                          selectedPlayers.filter(
                            (id: string) => id !== player.playerId
                          )
                        );
                        return;
                      }
                      if (player.playerId) {
                        setSelectedPlayers([
                          ...selectedPlayers,
                          player.playerId,
                        ]);
                      }
                    }}
                  >
                    <div role="td">{player.total.pos}</div>
                    <div role="td">
                      {player.nationality && (
                        <CountryFlag
                          countryCode={player.nationality}
                          style={{
                            fontSize: windowSize.width < 960 ? "1.6em" : "2em",
                          }}
                          svg
                        />
                      )}
                    </div>
                    <div
                      role="td"
                      className="!flex-[4] !text-left md:!flex-[5] !justify-start"
                    >
                      {player.playername}
                    </div>
                    {windowSize.width > 860 &&
                      player.holes.map((hole) => (
                        <div role="td" key={hole.holeNumber}>
                          {convertMToFeet(hole.distanceToPin)}
                        </div>
                      ))}
                    <div role="td" className="md:pr-[15px]">
                      {convertMToFeet(player.total.distanceToPin)}
                    </div>
                  </div>
                  <motion.div
                    role="tr"
                    className={clsx(
                      "overflow-hidden cursor-default",
                      "opacity-0 h-0",
                      "[&>*]:bg-tm-c-gray-800 [&>*]:m-5 [&>*]:rounded-[10px] [&>*]:overflow-hidden md:[&>*]:bg-none md:[&>*]:m-0 md:[&>*]:rounded-none md:[&>*]:overflow-auto",

                      {
                        "bg-tm-c-gray-900": selectedPlayers.includes(
                          player.playerId
                        ),
                      }
                    )}
                    variants={groupInnerVariants}
                    initial="initial"
                    animate={
                      selectedPlayers.includes(player.playerId)
                        ? "open"
                        : "initial"
                    }
                    exit="initial"
                  >
                    <div>
                      <div
                        role="thead"
                        className="flex justify-between bg-tm-c-gray-700 px-2.5 py-0 gap-[5px] md:gap-2.5 lg:p-0"
                      >
                        {windowSize.width < 861 &&
                          tournamentData?.data.data.node.rounds[
                            isMultiRoundTournament ? 1 : 0
                          ].embeddedGame.closestToPin.holes.map(
                            (hole: number) => (
                              <div
                                role="th"
                                className="flex-1 text-center justify-center"
                                key={hole}
                              >
                                Hole {hole}
                              </div>
                            )
                          )}
                      </div>
                      <div
                        role="tr"
                        className="flex justify-between w-full gap-[5px] md:gap-2.5 py-0 px-2.5 lg:p-0"
                      >
                        {windowSize.width < 861 &&
                          player.holes.map((hole) => (
                            <div
                              role="td"
                              className="flex items-center justify-center flex-1 relative text-center text-xs md:text-[15px] py-[15px] px-0"
                              key={hole.holeNumber}
                            >
                              {convertMToFeet(hole.distanceToPin)}
                            </div>
                          ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              );
            }
          );
        })}
        {isLoading && (
          <LeaderboardLoading columns={6} loadingLayout="default" />
        )}
      </div>
    </div>
  );
};

export default CTPLeaderboard;
